import {
    CATEGORY_LABELS,
    CHANNEL_LABELS,
    GLOBAL_SEARCH_NAVIGATION_STATE_KEY,
} from "../constants/globalSearchConstants.js";
import { MessageDirection, MessageDirectionLabels } from "../../pages/MessageUtils.jsx";
import { fromNow } from "../../pages/utils/dateUtils.js";
import { capitalizeFirstLetter } from "../../pages/utils/commonUtils.js";
import {
    MAX_RECENT_SEARCHES_TO_STORE_IN_THE_LOCAL_STORAGE,
    GLOBAL_SEARCH_RECENT_SEARCHES_KEY,
} from "../constants/globalSearchConstants.js";

const constructGlobalSearchRecentSearchesKey = (osyncUserId) => `${GLOBAL_SEARCH_RECENT_SEARCHES_KEY}-${osyncUserId}`;

function getGlobalSearchRecentSearches(osyncUserId) {
    try {
        const recentSearchedWords = window?.localStorage?.getItem(constructGlobalSearchRecentSearchesKey(osyncUserId));
        if (!recentSearchedWords) return [];
        const parsedRecentSearchedWords = JSON.parse(recentSearchedWords);
        return Array.isArray(parsedRecentSearchedWords) ? parsedRecentSearchedWords?.slice(0, MAX_RECENT_SEARCHES_TO_STORE_IN_THE_LOCAL_STORAGE) : [];
    } catch {
        return [];
    }
}

function addGlobalSearchRecentSearchIntoLocalStorage(newRecentSearchQuery, osyncUserId) {
    const trimmedNewRecentSearchQuery = newRecentSearchQuery?.trim();
    if (!trimmedNewRecentSearchQuery || trimmedNewRecentSearchQuery?.length < 2) return;
    try {
        const currentRecentSearchedWords = getGlobalSearchRecentSearches(osyncUserId);
        const updatedRecentSearchedWords = [trimmedNewRecentSearchQuery, ...currentRecentSearchedWords?.filter((recentSearchQuery) => recentSearchQuery !== trimmedNewRecentSearchQuery)].slice(
            0,
            MAX_RECENT_SEARCHES_TO_STORE_IN_THE_LOCAL_STORAGE
        );
        window?.localStorage?.setItem(constructGlobalSearchRecentSearchesKey(osyncUserId), JSON.stringify(updatedRecentSearchedWords));
    } catch {
        // ignore
    }
}

function buildGlobalSearchResultDescription(searchResultObject) {
    if (!searchResultObject) return "";
    const categoryName = CATEGORY_LABELS?.[searchResultObject?.type] || searchResultObject?.type;
    if (searchResultObject?.type === "message" || searchResultObject?.type === "note") {
        const descriptionParts = [categoryName];
        if (searchResultObject?.extra?.conversation_title) descriptionParts.push(searchResultObject?.extra?.conversation_title);
        if (searchResultObject?.extra?.direction != null) {
            const directionLabel =
                searchResultObject?.extra?.direction === MessageDirection.INBOUND
                    ? MessageDirectionLabels[MessageDirection.INBOUND]
                    : searchResultObject?.extra?.direction === MessageDirection.OUTBOUND
                      ? MessageDirectionLabels[MessageDirection.OUTBOUND]
                      : null;
            if (directionLabel) descriptionParts.push(directionLabel);
        }
        if (searchResultObject?.extra?.channel_category != null) {
            const channelLabel = CHANNEL_LABELS[searchResultObject?.extra?.channel_category];
            if (channelLabel) descriptionParts.push(channelLabel);
        }
        const timeAgo = fromNow(searchResultObject?.createdAt, true);
        if (timeAgo) descriptionParts.push(timeAgo);
        return descriptionParts?.filter(Boolean)?.join(" · ");
    }
    return [capitalizeFirstLetter(categoryName), searchResultObject?.description]?.filter(Boolean)?.join(" · ");
}

/**
 * Wraps query word matches in the title (or other string) with <mark> for global search UI.
 * Query is split on whitespace; each word is matched case-insensitively. Regex metacharacters in
 * the query are escaped so they are matched literally.
 *
 * @param {string} displayText - e.g. result title shown in the dropdown
 * @param {string} globalSearchQuery - raw search text from the input
 * @returns {string|Array} plain string if nothing to highlight, else an array of strings and <mark> elements
 */
function highlightMatchingText(displayText, globalSearchQuery) {
    // Nothing to search or nothing to show — return as-is (empty string if displayText missing)
    if (!displayText || typeof displayText !== "string" || !globalSearchQuery?.trim()) {
        return displayText ?? "";
    }
    const trimmedGlobalSearchQuery = globalSearchQuery?.trim();
    if (!trimmedGlobalSearchQuery) return displayText;

    // "foo  bar" -> ["foo", "bar"]; each token is highlighted separately
    const words = trimmedGlobalSearchQuery?.split(/\s+/).filter(Boolean);
    if (words?.length === 0) return displayText;

    // Build one alternation pattern: foo|bar. Escape .*+? etc. so "a+b" finds the literal text "a+b"
    const specialCharactersEscapedGlobalSearchQuery = words
        ?.map((word) => word?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        ?.join("|");
    

    // Split title around matches; capturing group keeps matched chunks in the array (gi = all matches, ignore case)
    const splittedPartsByMatched = String(displayText)?.split(new RegExp(`(${specialCharactersEscapedGlobalSearchQuery})`, "gi"));

    // Only the first occurrence per distinct word (case-insensitive) gets <mark>; repeats of the same word stay plain
    const highlightedSegments = new Set();
    return splittedPartsByMatched?.map((part, index) => {
        const matchedPartInLowerCase = part?.toLowerCase();
        const matchedWord = words?.find((word) => matchedPartInLowerCase === word?.toLowerCase());
        if (matchedWord && !highlightedSegments?.has(matchedPartInLowerCase)) {
            highlightedSegments?.add(matchedPartInLowerCase);
            return (
                <mark key={index} className="hs-color-bg-light-violet hs-color-violet rounded-1 px-1 py-0 border-0">
                    {part}
                </mark>
            );
        }
        return part;
    });
}

function getPathForTheSelectedResult(clickedSearchResult) {
    if (!clickedSearchResult?.type || !clickedSearchResult?.sourceId) return null;
    switch (clickedSearchResult?.type) {
        case "conversation":
            return `/inbox/${clickedSearchResult?.sourceId}`;
        case "message": {
            if (!clickedSearchResult?.associatedId) return null;
            return `/inbox/${clickedSearchResult?.associatedId}`;
        }
        case "note": {
            if (!clickedSearchResult?.associatedId) return null;
            return `/inbox/${clickedSearchResult?.associatedId}`;
        }
        case "contact":
            return `/contacts/${clickedSearchResult?.sourceId}`;
        default:
            return null;
    }
}

function getGlobalSearchNavigationState(clickedSearchResult) {
    switch (clickedSearchResult?.type) {
        case "message": {
            if (!clickedSearchResult?.sourceId || clickedSearchResult?.extra?.logTime == null) {
                return undefined;
            }
            return {
                [GLOBAL_SEARCH_NAVIGATION_STATE_KEY]: {
                    globalSearchNavigationStateMessageId: clickedSearchResult.sourceId,
                    globalSearchNavigationStateLogTime: String(clickedSearchResult.extra.logTime),
                },
            };
        }
        case "note": {
            if (!clickedSearchResult?.sourceId) {
                return undefined;
            }
            return {
                [GLOBAL_SEARCH_NAVIGATION_STATE_KEY]: {
                    globalSearchNavigationStateNoteId: clickedSearchResult.sourceId,
                },
            };
        }
        default:
            return undefined;
    }
}

export {
    getGlobalSearchRecentSearches,
    addGlobalSearchRecentSearchIntoLocalStorage,
    buildGlobalSearchResultDescription,
    highlightMatchingText,
    getPathForTheSelectedResult,
    getGlobalSearchNavigationState,
};